import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@/i18n.ts';
import '@/styles/global.css';
import '@/styles/standardizer.css';
import '@/styles/themes.css';
import { initAppearance } from '@/theme/appearance';
import { initTileGrowth } from '@/theme/tileSizing';
import { initForceScenesOpen } from '@/theme/boardScenesOpen';
import { applyMacTitlebarInset } from '@/theme/macTitlebarInset';
import { applyHostOsFlags } from '@/platform/hostOsFlags';
import App from '@/App.tsx';
import { ErrorBoundary } from '@/components/ErrorBoundary.tsx';
import { DevBanner } from '@/components/DevBanner.tsx';
import { installPaintedFlag } from '@/bridge/paintedFlag';
import { installFileDragGovernor } from '@/dragDropGovernor';
import { installDragWakeKeepalive } from '@/dragWake';
import { installScrollWakeKeepalive } from '@/scrollWake';
import { installTapWakeKeepalive } from '@/tapWake';
import { installClipboardPasteFallback } from '@/clipboardPasteFallback';
import { installEnvSafeAreaWatcher } from '@/platform/foldStateStore';
import { installViewportTransitionListeners } from '@/platform/viewportTransition';
import { installPrevisDebugHook } from '@/debug/previsDebugHook';

// ── Debug log capture (Ctrl+Shift+L → clipboard) ─────────────────────
{
  const _logs: string[] = [];
  const MAX = 500;
  const ts = () => new Date().toISOString().slice(11, 23);
  const push = (level: string, args: unknown[]) => {
    const line = `[${ts()}] ${level}: ${args.map(a => (typeof a === 'string' ? a : JSON.stringify(a, null, 0))).join(' ')}`;
    _logs.push(line);
    if (_logs.length > MAX) _logs.shift();
  };
  const origLog = console.log;
  const origWarn = console.warn;
  const origError = console.error;
  console.log = (...args: unknown[]) => { push('LOG', args); origLog.apply(console, args); };
  console.warn = (...args: unknown[]) => { push('WRN', args); origWarn.apply(console, args); };
  console.error = (...args: unknown[]) => { push('ERR', args); origError.apply(console, args); };
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'L') {
      e.preventDefault();
      const text = _logs.join('\n');
      navigator.clipboard.writeText(text).then(
        () => origLog('[DebugCapture] Copied', _logs.length, 'log lines to clipboard'),
        (err) => origWarn('[DebugCapture] Clipboard write failed:', err),
      );
    }
  });
}

// ── Global FILE-drag governor ────────────────────────────────────────
// Kills the navigate-to-dropped-file wedge in the embedded WebView, and refuses the drag
// outright everywhere the pointer is NOT over a tagged drop zone so the OS stops painting the
// green "+" copy badge over the whole app. Every legitimate zone carries
// `data-file-drop-target` while its handlers are live — see dragDropGovernor.ts.
installFileDragGovernor();

// ── Drag KEEPALIVE (embedded only) ───────────────────────────────────
// A file drag over the WebView is invisible to Unity's input system, so 0.6s into one the host's
// idle frame-rate governor throttles the main thread the WebView depends on and drag delivery,
// rAF and compositing all starve. This pings the host with a payload-free `dragActivity` while a
// Files drag is in flight. Installed next to the governor because they observe the same two events,
// but kept a separate module: that one is drag POLICY and is deliberately bridge-free.
// installDragWakeKeepalive() self-gates on capabilities.bridge — a no-op standalone / in the
// debug harness, where there is no host to wake.
installDragWakeKeepalive();

// ── Scroll KEEPALIVE (embedded only) ─────────────────────────────────
// Scrolling is the drag problem's twin: wheel events over the WebView never reach Unity's input
// system, plain scrolling sends no bridge traffic, and the native trackpad monitor deliberately
// skips over-WebView scrolls — so on macOS the idle governor keeps the main thread throttled
// (20fps + 45ms/frame Thread.Sleep) through the whole gesture and wheel delivery chunks with it.
// Device-measured 20260824: wheel 26–32/sec against a 60–120/sec trackpad stream, rAF 25–32/sec,
// 210–265ms delivery-age spikes. This pings a payload-free `scrollActivity` (≤1/300ms) while
// wheel/scroll events flow. Self-gates on capabilities.bridge like the drag keepalive above.
installScrollWakeKeepalive();

// ── Tap KEEPALIVE + settle burst (embedded only) ─────────────────────
// The third face of the same governor problem: no tap inside the WebView ever wakes the host
// (pointer events are consumed by the WKWebView; tap-to-select is a pure store write with no
// bridge verb), so tap-triggered transitions — shot selection's panel slide + scene expand +
// 464-cell re-wrap — start against an already-throttled host and their settle outlasts the one
// 0.6s window a single message buys. The listener pings on any pointerdown; board interactions
// with a long settle additionally call wakeHostThroughSettle() for a bounded ~1.8s hold.
// Self-gates on capabilities.bridge like its two siblings above.
installTapWakeKeepalive();

// ── Clipboard PASTE fallback ─────────────────────────────────────────
// WKWebView's native paste can fail when its text input system isn't fully activated — the Cmd+V
// reaches the page, no `paste` event fires, nothing is inserted. This shim used to live inside
// useKeyboardShortcuts, whose only call site is TimelineView, so it covered the Timeline route and
// nothing else: the board's Enhance dialog, the Script tab, Screenplay Manager, Projects and Sign
// In all had no paste assistance in the same WebView. Installed here it covers every route — and
// EXACTLY ONCE, which is the point: SceneBoardPage mounts TimelineView on its Timeline tab, so a
// second copy still living in the hook would fire alongside this one and paste twice there.
// It self-suppresses whenever a native `paste` event does arrive — see clipboardPasteFallback.ts.
installClipboardPasteFallback();

initAppearance();

// Same native-authority boot as initAppearance (URL param → localStorage mirror → default), for
// the preview-grid tile-growth preference. Deliberately NOT duplicated into index.html's
// pre-paint script: a wrong first frame here is a reflow, not a colour flash.
initTileGrowth();

// 20260909 (owner) — same native-authority boot again, for the Board's "force scenes open" row.
// Before first render because it decides the board's very first layout (every scene expanded, no
// hidden overflow row), so resolving it later would cost a full re-wrap of every grid.
initForceScenesOpen();

// Before first render, so the top bar is laid out with the traffic-light inset already applied
// rather than reflowing once. A no-op off macOS and outside the embedded webview.
applyMacTitlebarInset();

// 20260923 - same reason, for the iPadOS 15-18 multitasking pill: before first render so the bar is
// laid out with the inset already applied. A no-op everywhere but an iPad below iPadOS 26.
applyHostOsFlags();

// 20260923 - window.__previsDebug (stores, state(), register): the handle the runtime MCP server's
// evaluate_javascript and Safari's Web Inspector use to read and drive the app. Dev bundles only —
// a no-op in Prod. Before createRoot because lazily loaded modules (SceneBoardPage's escOverlayOpen)
// register into it as they evaluate, so it must already exist. It registers no event listeners, so
// it cannot get ahead of the viewport-transition listener below.
installPrevisDebugHook();

// ── 20260920 - "THE DEVICE IS CHANGING SHAPE": the signal every layout snaps on ───────────
// A rotation, a fold/unfold or a window resize moves the viewport under a layout that is already on
// screen, and the layout changes that follow are the app catching up — they must SNAP, not glide.
// FIRST, and before createRoot, because that is the whole guarantee: the listener is capture-phase
// and registered before anyone else's, so the flag is already up when a layout hook re-reads its
// media query inside the same resize event and commits in that event (React 19 treats a resize
// listener as discrete and a useSyncExternalStore update as SyncLane, so it commits before the same
// paint). Raising it one line later would be too late to stop the glide. See
// platform/viewportTransition.ts; the disposer is dropped on purpose, exactly as below.
installViewportTransitionListeners();

// ── 20260919 - IPHONE DUO SIDE DOCK: keep env(safe-area-inset-*) current ──────────────────
// The store SEEDS itself from env() at creation, and this installs everything that keeps it current
// afterwards. `resize` is the load-bearing listener — a fold or unfold is a resize with no
// orientationchange and no orientation change at all, and this WebView drops MediaQueryList/
// ResizeObserver deliveries across a rotation (the symptom useRegularWidthLayout.ts and
// useAdaptiveGrid.ts already work around).
//
// The seed alone is NOT enough, and cannot be made enough: in the embedded WebView the safe-area
// numbers come back from the UI process over IPC, so the sample taken at module-evaluation time
// reads zeros however hard we flush layout. This call also arms a finite post-paint settle ladder
// and a ResizeObserver on the probe, which is what actually lands the reserved-strip layout — a
// frame or two after first paint, rather than never (the owner's 20260919 report: controls stuck in
// the top bar until a physical fold/unfold). Must be BEFORE createRoot so the first ladder rung is
// already pending when the first frame commits.
//
// Installed here rather than in a hook so the answer is correct app-wide with no component owning
// it, exactly like applyMacTitlebarInset above — and so StrictMode's dev double-invoke of effects
// cannot install two ladders and two observers.
// Self-gates on `typeof window` like the three keepalives, so importing this entry costs nothing in
// a headless host; the disposer is dropped on purpose — these live as long as the document.
installEnvSafeAreaWatcher();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <DevBanner />
    <ErrorBoundary fullPage>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

// LAST, and after render() on purpose. Raises window.__previsAppPainted once the browser has
// actually composited the app's first frame — the answer WebViewBlankScreenGuard and
// WebViewPaintProbe ask for — and, in the same double-rAF, takes down index.html's pre-bundle
// holding spinner (20260921). One cut from the ring to the app; see bridge/paintedFlag.ts.
installPaintedFlag();
